import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Feature, FeatureStore, FeatureStoreSelectionStrategy, Layer } from '@igo2/geo';
import { Constantes } from 'src/app/shared/constantes';
import { StatutDossierTerrainGdf } from 'src/app/shared/models/dossier-terrain-gdf';
import { RechercheGdfService } from 'src/app/shared/services/recherche-gdf.service';
import { SIGTRechercheGdfBaseComponent } from '../_sigt-recherche-gdf.base.component';
import { and, equalTo, or } from 'ol/format/filter';
import { ZzzConfigService } from 'src/app/zzz/core/config/config.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { EntityRecord, EntityTableButton, EntityTableColumnRenderer, EntityTableTemplate } from '@igo2/common/entity';
import { LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import * as olstyle from 'ol/style';
import IsLike from 'ol/format/filter/IsLike';
import Filter from 'ol/format/filter/Filter';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';

@Component({
    selector: 'app-dossier-terrain-gdf',
    templateUrl: './dossier-terrain-gdf.component.html',
    styleUrls: ['./dossier-terrain-gdf.component.scss'],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        TranslateModule
    ]
})
export class DossierTerrainGdfComponent extends SIGTRechercheGdfBaseComponent implements OnInit {
  propertyKeyAndTitle: { [key: string]: string };
  template: EntityTableTemplate;
  templateDroit: EntityTableTemplate;

  #configService = inject(ZzzConfigService);
  form: UntypedFormGroup;
  listStatutsDossiers: StatutDossierTerrainGdf[] = [];
  caracteristiques: Feature[] = [];
  #valeurStatutDefaut: string = 'A';
  styleDroits: olstyle.Style | ((feature, resolution) => olstyle.Style);
  
  get urlBaseApi(): string {
    return this.#configService.get().authApiEndPoint + "/sigtapi";
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    languageService: LanguageService,
    rechercherGdfService: RechercheGdfService,
    httpClient: HttpClient,
    cdRef: ChangeDetectorRef,
    messageService: MessageService,
  ) {
    super(rechercherGdfService, httpClient, messageService, languageService, cdRef);
    this.propertyKeyAndTitle = this.rechercherGdfService.getPropertyKeyAndTitle('dossierTerrainGdf');
    this.template = this.rechercherGdfService.getEntityTableTemplate(
      'dossierTerrainGdf',
        (row, record) => {
          if (record?.state?.selected) {
            this.rechercherDossier(row);
          }
        }
      );
    this.templateDroit = this.rechercherGdfService.getEntityTableTemplate(
      'droitGdf',
        (row, record) => {
        if (record?.state?.selected) {
          const olFeature = record?.entity.ol;
          const store:FeatureStore = olFeature?.get("_featureStore");
          const featureStoreSelectionStrategy = store.getStrategyOfType(FeatureStoreSelectionStrategy) as FeatureStoreSelectionStrategy;
          const layer = featureStoreSelectionStrategy?.overlayStore.layer;
          this.rechercherGdfService.selectLegendAndPrintMap(row, layer);
        }
      }
    );
  }

  ngOnInit(): void {
    this.style = new olstyle.Style({
      image: new olstyle.Circle({
        radius: 3.5,
        stroke: new olstyle.Stroke({
          width: 1,
          color: [0, 0, 0, 1]
        }),
        fill: new olstyle.Fill({
          color: [247, 15, 11, 1]
        })
      })
    });
    this.getLayer("DossierTerrain");
    this.form = this.formBuilder.group({
      numeroDossier: ["", [Validators.required, Validators.pattern("[0-9]+")]],
      annee: ["", [Validators.pattern("[0-9]+")]],
      section: ["", [Validators.pattern("[0-9]+")]],
      statut: [""]
    }, { validators: [this.validerNumeroDossier.bind(this)] });

    this.messageApplication.grilleVide = Constantes.MessageApplicatif.AucunElement_6I;
    this.messageApplication.erreurCouche = Constantes.MessageApplicatif.CoucheIndisponible_16T;
    this.messageApplication.obligatoireNumeroDossier = Constantes.MessageApplicatif.ValeurObligatoire_1E
      .replace("{0}", this.getCleanKey('dossierTerrainGdf.numeroDossier'));

    this.chargerStatus();
    
    this.styleDroits = (feature) => new olstyle.Style({
        image: new olstyle.Icon({
          src: this.createDroitSVG("#FFFFFF", "#E69800"),
          scale: 4,
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction'
        }),
        text: new olstyle.Text({
          text: feature.get('No dossier') || '', // Label from feature attribute
          font: '32px Calibri,sans-serif',
          fill: new olstyle.Fill({ color: '#000' }),
          stroke: new olstyle.Stroke({ color: '#fff', width: 2 }),
          offsetY: -80,
          textAlign: 'center'
        })
      });


  }

  chargerStatus() {
    //Les status sont chargés une seule fois
    this.httpClient.get<{ listeStatutVersion: StatutDossierTerrainGdf[] }>(this.urlBaseApi + "/gdf/dossier-terrain/statuts")
      .subscribe({
        next: reponse => {
          this.listStatutsDossiers = reponse.listeStatutVersion.sort((a, b) => a.nomStatut.localeCompare(b.nomStatut));
          this.defautStatut(this.listStatutsDossiers);
        },
        error: error => {
          this.messageService.error("ERREUR APPEL dossier-terrain/statuts");
          console.log(error);
        }
      });
  }

  defautStatut(statuts: StatutDossierTerrainGdf[]) {
    if (String(this.form.get('statut').value) === '') {
      if (statuts.filter(statut => statut.codeStatut === this.#valeurStatutDefaut).length > 0) {
        this.form.get("statut").setValue([this.#valeurStatutDefaut]);
      }
    }
  }

  formaterChampsFeaturePourResultatRecherche(features: Feature[]): Feature[] {
    return this.rechercherGdfService.formatFeaturesForTable('dossierTerrainGdf', features);
  }

  buildFilter(): Filter {
    let numeroDossier = this.form.get("numeroDossier").value;
    let annee = this.form.get("annee").value;
    let section = this.form.get("section").value;
    const statut = this.form.get("statut").value;

    let filter: Filter;
    let subFfilter: Filter[] = [];

    numeroDossier = this.supprimerZeroAuDebut(numeroDossier);
    subFfilter.push(
      or(new IsLike('TER_NO_DOSSI', numeroDossier + '%', '%', undefined, undefined, false),
        new IsLike('TER_NO_DOSSI', '0' + numeroDossier + '%', '%', undefined, undefined, false),
        new IsLike('TER_NO_DOSSI', '00' + numeroDossier + '%', '%', undefined, undefined, false),
        new IsLike('TER_NO_DOSSI', '000' + numeroDossier + '%', '%', undefined, undefined, false),
        new IsLike('TER_NO_DOSSI', '0000' + numeroDossier + '%', '%', undefined, undefined, false)
      )
    );

    if (annee !== null) {
      if (annee !== '') {
        subFfilter.push(
          or(equalTo('TER_AN_DOSSI', annee),
            equalTo('TER_AN_DOSSI', '0' + annee),
            equalTo('TER_AN_DOSSI', '00' + annee))
        );
      }
    }

    if (section !== null) {
      if (section !== '') {
        subFfilter.push(
          or(equalTo('TER_NO_SECTI', section),
            equalTo('TER_NO_SECTI', '0' + section),
            equalTo('TER_NO_SECTI', '00' + section),
            equalTo('TER_NO_SECTI', '000' + section))
        );
      }

    }

    if (statut !== undefined && statut.length > 0) {
      let eqFilters = statut.map(f => equalTo('TER_STATU_VERSI', f));
      const filtersOr = eqFilters.length > 1 ? or(...eqFilters) : eqFilters[0];
      subFfilter.push(filtersOr);
    }

    filter = subFfilter.length > 1 ? and(...subFfilter) : subFfilter[0];
    return filter;
  }

  rechercherDossier(row):void {
    this.rechercherGdfService.checkWMSLayers("Droit").subscribe({
          next: (layer: Layer) => {
              if (layer) {
                this.rechercherGdfService.hideAllGdfCreatedLayers();
                this.rechercherGdfService.rechercherFeatures(layer, this.buildFilterDroit(row)).subscribe({
                next: features => {
                    if (!features.length) {
                    this.messageService.info(this.messageApplication.grilleVide);
                    return;
                    }

                    this.rechercherGdfService.addSearchResultsToMap(
                    layer.options.id.toString(),
                    this.rechercherGdfService.formatFeaturesForTable('droitGdf', features),
                    this.cdRef,
                    this.templateDroit,
                    this.styleDroits
                    );
                },
                error: err => {
                    this.messageService.error(this.messageApplication.erreurCouche);
                    console.log(err);
                }
                });
              } else {
                  console.log('Layer not found');
              }
          },
          error: (error) => {
              console.error('Error loading layer:', error);
          }
      });
  }

  buildFilterDroit(row):Filter {
    let filter: Filter = undefined;
    let subFilter: Filter[] = [];
    if (row.hasOwnProperty("properties")) {
      const noDossierRecherche = row.properties[this.propertyKeyAndTitle.noDossier];
      const parts = noDossierRecherche.split(' ');
      const numeroDossier = parseInt(parts[0], 10);
      const anneeDossier = parseInt(parts[1],10);
      const section = parseInt(parts[2],10);
    filter = 
      and(
        equalTo('TER_NO_DOSSI', numeroDossier),
        equalTo('TER_AN_DOSSI', anneeDossier),
        equalTo('TER_NO_SECTI', section),
        or(
          equalTo('DRT_STATU', 'A'),
          equalTo('DRT_STATU', 'T')
        )
      );
    }
   return filter;

  }
  initialiserChamps(): void {
    this.form.setValue({
      numeroDossier: '',
      annee: '',
      section: '',
      statut: [this.#valeurStatutDefaut]
    });
    this.form.get('numeroDossier').setErrors(null, { emitEvent: false });
  }

  validerNumeroDossier(control: FormGroup): any {
    if (control.value.numeroDossier) {
      const numeroDossier = control.controls["numeroDossier"].value;
      if (numeroDossier === null || numeroDossier?.length === 0) {
        return { notValid: true };
      }
      return null;
    }
    return { notValid: true };
  }

  createDroitSVG(strokeColor:string, fillColor:string):string {
    const svgLinearIconHome5 = `<svg xmlns="http://www.w3.org/2000/svg" width= "20" height="20" viewBox="0 0 1000 1000">
                                  <g><polygon points="805.8,335.5 805.8,138.3 699.2,138.3 699.2,250.3 500,90.9 10,482.9 144.9,482.9 144.9,909.1 373.7,909.1 373.7,625 626.3,625 626.3,909.1 855.1,909.1 855.1,482.9 990,482.9 "
                                  fill="${fillColor}" stroke="${strokeColor}" stroke-width="10"
                                  /></g>
                                </svg>`;
    const svgLocationOn = `<svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 -960 960 960">
                            <path d="M480.21-480Q510-480 531-501.21t21-51Q552-582 530.79-603t-51-21Q450-624 429-602.79t-21 51Q408-522 429.21-501t51 21ZM480-96Q323.03-227.11 245.51-339.55 168-452 168-549q0-134 89-224.5T479.5-864q133.5 0 223 90.5T792-549q0 97-77 209T480-96Z"
                            fill="${fillColor}"/>
                            <path d="M480-480q33 0 56.5-23.5T560-560q0-33-23.5-56.5T480-640q-33 0-56.5 23.5T400-560q0 33 23.5 56.5T480-480Zm0 294q122-112 181-203.5T720-552q0-109-69.5-178.5T480-800q-101 0-170.5 69.5T240-552q0 71 59 162.5T480-186Zm0 106Q319-217 239.5-334.5T160-552q0-150 96.5-239T480-880q127 0 223.5 89T800-552q0 100-79.5 217.5T480-80Zm0-480Z"
                            fill="${strokeColor}"
                            />
                            </svg>`;
    const svgDataUri = 'data:image/svg+xml;utf8,' + encodeURIComponent(svgLocationOn);
    return svgDataUri;
  }
}